import React from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { setHideExpandedPlayer } from '@store/audio/AudioMeta';
import { webappUriPath } from '@utils/url';
import type { CommentType } from '../../types/comments';

const AVATAR_SIZE = 56;
const DEFAULT_CONTENT_HEIGHT = 76;
const MAX_COMMENT_HEIGHT = 18;

const EXPANDED_GRADIENT = 'linear-gradient(180deg,rgba(0, 0, 0, 0) 0%,   #000 100%)';
const COLLAPSED_GRADIENT = 'linear-gradient(180deg,rgba(0, 0, 0, 0) 90%,   #000 100%)';

export const PlayerCommentCard = React.memo(function PlayerCommentCard({
  comment,
}: {
  comment: Omit<CommentType, 'commentTimestamp'>;
}) {
  const {
    username,
    publicAddress,
    avatarUrl,
    webappUri,
    isArtist,
    isEggWinner,
    updatedAt,
    commentText,
  } = comment;

  const datePurchased = updatedAt ? new Date(updatedAt) : null;

  const [hide, setHide] = React.useState<boolean>(false);
  const [isExpanded, setExpanded] = React.useState<boolean>(false);

  const contentHeight = isExpanded ? 'auto' : `${DEFAULT_CONTENT_HEIGHT}px`;

  const commentRef = React.useRef<HTMLDivElement>(null);

  useSafeLayoutEffect(() => {
    if (!commentRef.current) return;
    const { scrollHeight } = commentRef.current;
    if (scrollHeight > MAX_COMMENT_HEIGHT) {
      setHide(true);
    }
  }, []);

  return (
    <Container
      initial={{
        background: COLLAPSED_GRADIENT,
      }}
      animate={{
        //this is to make sure that comment is still readable when expanded
        background: isExpanded ? EXPANDED_GRADIENT : COLLAPSED_GRADIENT,
      }}
      transition={{
        duration: 0.3,
      }}
    >
      <AvatarWrapper>
        <Avatar
          src={avatarUrl}
          size={AVATAR_SIZE}
          walletAddress={publicAddress}
          username={username}
          webappUri={webappUri}
          backgroundColor="$black"
          borderRadius={6}
          onClick={() => {
            setHideExpandedPlayer(true);
          }}
        />
      </AvatarWrapper>

      <RightContent
        initial={{
          //we set initial height to 0 to prevent the animation from playing on load and showing all content
          height: !hide ? DEFAULT_CONTENT_HEIGHT : 'auto',
        }}
        animate={{
          height: contentHeight,
        }}
        transition={{
          duration: 0.3,
        }}
        onTap={() => {
          //if the comment is short enough, we don't want to expand it
          if (!hide) return;
          setExpanded(prev => !prev);
        }}
      >
        <Wrapper>
          <UserWrapper onClick={() => setHideExpandedPlayer(true)}>
            <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
              <User>
                <Username>{username}</Username>
                {isArtist && <VerifiedTooltip noTooltip size={18} />}
              </User>
            </AccessibleLink>
            {isEggWinner && (
              <NextFutureImage
                width={24}
                height={24}
                src="/images/comments/GoldenEgg.png"
                alt="Golden Egg"
              />
            )}
          </UserWrapper>

          {datePurchased && <DateText>{format(datePurchased, 'MMMM d, yyyy')}</DateText>}
        </Wrapper>

        <Comment ref={commentRef}>{commentText}</Comment>
      </RightContent>
      {hide && (
        <Gradient
          initial={{ opacity: isExpanded ? 0 : 1 }}
          animate={{ opacity: isExpanded ? 0 : 1 }}
          transition={{ duration: 0.3 }}
          onClick={() => setExpanded(prev => !prev)}
        />
      )}
    </Container>
  );
});

const Container = styled(motion.div, {
  display: 'flex',
  gap: 16,
  width: '100%',
  padding: '12px 12px 0px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  position: 'relative',
});

const RightContent = styled(motion.div, {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: '100%',
});

const AvatarWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
});

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const UserWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: 4,
  height: 24,
  marginTop: -6,

  '& > a': {
    width: 'fit-content',
    maxWidth: '70%',
  },
});

const DateText = styled('p', {
  base: 'XS',
  color: '$base100',
});

const User = styled('div', {
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
  gap: 6,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
  height: 24,
});

const Username = styled('p', {
  title: 'XS',
  fontWeight: 500,
  color: '$white',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  //lineheight is pushing it up and making it off center
  lineHeight: 'unset',
  height: 18,
});

const Comment = styled('p', {
  base: 'M',
  color: '$white',
  fontWeight: 500,
});

const Gradient = styled(motion.div, {
  position: 'absolute',
  bottom: -4,
  right: 0,
  width: '100%',
  height: 20,
  background: 'linear-gradient(180deg,rgba(0, 0, 0, 0.1) 0%,  rgba(0, 0, 0, 0.8) 100%)',
});
